import { Close, Mic, MicOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export enum MicAccessStatus {
  Waiting,
  Recording,
  NotAllowed,
}

interface MicVolumeModalProps {
  open: boolean;
  onClose: () => void;
}

const MicVolumeModal = ({ open, onClose }: MicVolumeModalProps) => {
  const { t } = useTranslation();
  const [volume, setVolume] = useState<number>(0);
  const [status, setStatus] = useState<MicAccessStatus>(MicAccessStatus.Waiting);
  const streamRef = useRef<MediaStream | null>(null);
  const animationIdRef = useRef<number | null>(null);

  const startMonitoring = async () => {
    await navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        setStatus(MicAccessStatus.Recording);
        streamRef.current = stream;

        const audioContext = new window.AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.fftSize);

        const updateVolume = () => {
          if (!analyser) return;
          analyser.getByteTimeDomainData(dataArray);

          let sumSquares = 0;
          for (let i = 0; i < dataArray.length; i += 1) {
            const normalized = (dataArray[i] - 128) / 128;
            sumSquares += normalized * normalized;
          }
          const rms = Math.sqrt(sumSquares / dataArray.length);
          const volumeLevel = Math.round(rms * 300);

          setVolume(volumeLevel);
          animationIdRef.current = requestAnimationFrame(updateVolume);
        };

        updateVolume();
      })
      .catch((err) => {
        setStatus(MicAccessStatus.NotAllowed);
        console.error('Ошибка доступа к микрофону:', err);
      });
  };

  const stopMonitoring = () => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setStatus(MicAccessStatus.Waiting);
    setVolume(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      sx={{ '& .MuiPaper-root': { paddingX: 7 } }}>
      <DialogTitle variant='h28' sx={{ paddingX: 0, textAlign: 'center' }}>
        {t('microphone.title')}
      </DialogTitle>
      <IconButton onClick={onClose} sx={{ position: 'absolute', top: 2, right: 2 }}>
        <Close />
      </IconButton>
      <DialogContent>
        <Stack direction='row' spacing={2} justifyContent='center' alignItems='center'>
          {status === MicAccessStatus.Recording ? (
            <Mic fontSize='large' />
          ) : (
            <MicOff fontSize='large' />
          )}
          <Box sx={{ width: '250px' }}>
            <LinearProgress
              sx={{ height: 8 }}
              variant='determinate'
              value={volume > 100 ? 100 : volume}
            />
          </Box>
        </Stack>
        {status === MicAccessStatus.NotAllowed && (
          <Typography textAlign='center' sx={{ marginTop: 1 }}>
            Мы не можем получить доступ к&#160;микрофону. <br />
            Разрешите доступ в&#160;настройках браузера и&#160;попробуйте снова.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        {status === MicAccessStatus.Recording ? (
          <Button variant='contained' size='large' onClick={stopMonitoring}>
            {t('microphone.stopChecking')}
          </Button>
        ) : (
          <Button variant='contained' size='large' onClick={startMonitoring}>
            {t('microphone.startChecking')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MicVolumeModal;
